import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Service from '../services/Service';
import '../styles/PatientOrder.css';
import StatusTimeline from '../../../../Components/StatusTimeline/StatusTimeline';
import KitDetails from '../components/KitDetails';
import ClinicOrderDetails from '../components/ClinicOrderDetails';
import ReorderKitModal from '../components/ReorderKitModal';
import ReplacementDetails from '../components/ReplacementDetails';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

// SupplementOrder.fulfillment_status -> label shown in the Order Status column.
const SUPPLEMENT_STATUS_MAP = {
  not_submitted: 'Processing',
  submitted: 'Order Placed',
  in_production: 'In Production',
  partial: 'Partially Shipped',
  shipped: 'Shipped',
  delivered: 'Delivered',
  failed: 'Failed',
};

// Order.billing_type on clinic (B2B) orders -> label shown in the detail view.
const BILLING_TYPE_LABELS = {
  patient_pay: 'Patient Pay',
  clinic_pay: 'Clinic Pay',
  monthly_invoice: 'Monthly Invoice',
};

// Payment pill for clinic orders. "Billed to Practice" says who pays, not that
// SwimScore has been paid, so the pill follows the staff-only payment state.
const clinicPaymentPill = (order) => {
  // Replacement kits are sent on SwimScore's account; nobody is billed.
  if (order?.isReplacement) return { label: 'No charge', className: 'paid' };
  const payment = order?.b2bDetails?.billing?.payment;
  if (!payment) return { label: order?.financial_status, className: order?.financial_status?.toLowerCase() };
  if (payment.completed) return { label: payment.label, className: 'paid' };
  if (payment.state === 'failed' || payment.state === 'canceled') return { label: payment.label, className: 'failed' };
  if (payment.state === 'billed' || payment.state === 'invoiced') return { label: payment.label, className: 'invoiced' };
  return { label: payment.label, className: 'pending' };
};

// OK Capsule keeps its own address keys (addressee / province_name / postal_code /
// country_name); normalize them to the shape formatAddress renders.
const supplementAddress = (address) => {
  if (!address || !Object.keys(address).length) return null;
  return {
    name: address.addressee,
    address1: address.address1,
    address2: address.address2,
    city: address.city,
    province_code: address.province_name || address.province_code,
    zip: address.postal_code || address.zip,
    country: address.country_name || address.country,
  };
};

// Supplement orders come from the medication app (OK Capsule + Stripe
// subscriptions), so normalize them into the same shape this page renders.
const mapSupplementOrder = (order) => {
  const lineItems = order.line_items?.length ? order.line_items : [{ title: 'Supplement Pack' }];
  const supplyLabel = order.plan_label || (order.supply_days ? `${order.supply_days}-day supply` : null);
  return {
    id: order.id,
    order_number: order.order_number,
    created_at: order.created_at,
    financial_status: order.payment_status === 'successful' ? 'paid' : order.payment_status,
    fulfillment_status: SUPPLEMENT_STATUS_MAP[order.fulfillment_status] || 'Processing',
    total_price: order.amount,
    currency: (order.currency || 'usd').toUpperCase(),
    email: order.email,
    billing_address: null,
    shipping_address: supplementAddress(order.shipping_address),
    isRetest: false,
    isSupplement: true,
    supplementStatus: order.fulfillment_status,
    planLabel: supplyLabel,
    subscriptionStatus: order.subscription_status,
    okcapsuleOrderName: order.okcapsule_order_name,
    trackingNumber: order.tracking_number,
    trackingUrl: order.tracking_url,
    errorMessage: order.error_message,
    shipments: order.shipments || [],
    products: lineItems.map((item, i) => ({
      id: item.product_id || `${order.id}-${i}`,
      product_name: item.title || 'Supplement Pack',
      // One pack per order, so the amount charged is that line's price. With
      // multiple lines the per-line split isn't known — leave it to the totals.
      price: lineItems.length === 1 ? order.amount : '',
      quantity: 1,
      total_discount: '0.00',
      subtitle: supplyLabel,
    })),
    shipping_lines: [],
    total_tax: '0.00',
    total_discounts: '0.00',
    payment_gateway_names: 'stripe',
    db_order: {},
  };
};

// Intake email / JotForm submission tracking lives on the DB order — kit orders
// only. Supplements have no intake step.
const intakeState = (dbOrder) => {
  if (!dbOrder) return null;
  return {
    emailSent: !!dbOrder.intake_email_sent,
    emailSentAt: dbOrder.intake_email_sent_at || null,
    submitted: !!dbOrder.intake_submitted,
    submittedAt: dbOrder.intake_submitted_at || null,
  };
};

// Kits on an order that a replacement can be issued for.
const REPLACEABLE_KITS = ['mailman', 'tasso'];
const orderKitTypes = (type) => {
  if (Array.isArray(type)) return type;
  if (typeof type !== 'string' || !type) return [];
  try {
    const parsed = JSON.parse(type);
    return Array.isArray(parsed) ? parsed : [type];
  } catch {
    return [type];
  }
};
const canReorderKit = (order) =>
  !order.isSupplement && !!order.dbId && orderKitTypes(order.db_order?.type).some((kit) => REPLACEABLE_KITS.includes(kit));

const PatientOrders = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showReorderModal, setShowReorderModal] = useState(false);
  const location = useLocation();
  const patient = location.state?.patient || null;
  const [role, setRole] = useState(null);
  useEffect(() => {
    const token = sessionStorage.getItem('auth_token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setRole(decoded?.role || null);
      } catch (e) {
        setRole(null);
      }
    }
  }, []);
  // `fresh` bypasses the cached order list, e.g. after a replacement kit is issued.
  const loadOrders = useCallback(
    async ({ fresh = false } = {}) => {
      setLoading(true);
      // Kit/retest orders (Shopify) and supplement orders (OK Capsule) come from
      // different backends — fetch both and merge. One source failing must not
      // blank out the other.
      const [listRes, supplementRes] = await Promise.allSettled([Service.getOrderList(id, { fresh }), Service.getSupplementOrders(id)]);

      if (listRes.status === 'rejected' && supplementRes.status === 'rejected') {
        setError(listRes.reason?.response?.data?.message || 'Failed to load orders');
        setLoading(false);
        return [];
      }

      try {
        const rawOrders = listRes.status === 'fulfilled' ? listRes.value.data || [] : [];
        const formattedOrders = rawOrders.map((order) => {
          const isRetest = !!order['retest-order'];
          const base = isRetest ? order['retest-order'] : order;
          // Clinic/B2B orders never touch Shopify — the backend sends them in this
          // same shape flagged with is_non_shopify, with no billing snapshot.
          const isClinicOrder = !!base.is_non_shopify;
          const dbOrder = base.db_order || base;
          return {
            id: base.id,
            // Platform order id (Shopify rows carry the Shopify id in `id`).
            dbId: dbOrder.id || null,
            isReplacement: !!dbOrder.replacement_of,
            order_number: base.order_number || base.order_id,
            created_at: base.created_at,
            financial_status: base.financial_status || base.payment_status,
            fulfillment_status: base.fulfillment_status || 'Pending',
            status_detail: base.db_order?.status_detail || base.status_detail || order.db_status || { code: 'pending', label: 'Pending' },
            total_price: base.total_price || base.price || base.amount,
            currency: base.currency || 'USD',
            email: base.email,
            billing_address: base.billing_address,
            shipping_address: base.shipping_address,
            isRetest,
            // A replacement shows on its own row; the Clinic badge would read as a clinic purchase.
            isClinicOrder: isClinicOrder && !dbOrder.replacement_of,
            orderSource: base.order_source || null,
            clinicName: base.clinic_name || null,
            billingType: base.billing_type || null,
            paymentLink: base.payment_link || base.db_order?.payment_link || null,
            // Staff-only full B2B picture (clinic, orderer, billing, per-leg shipping).
            b2bDetails: base.b2b_details || null,
            products: (base.line_items || base.products || []).map((item) => ({
              id: item.id,
              product_name: item.name || item.product_name,
              price: item.price,
              quantity: item.quantity || 1,
              total_discount: item.total_discount || '0.00',
            })),
            shipping_lines: (base.shipping_lines || []).map((line) => ({
              title: line.title,
              price: line.price || line.discounted_price,
            })),
            total_tax: base.total_tax || '0.00',
            total_discounts: base.total_discounts || '0.00',
            tags: base.tags || [],
            payment_gateway_names: (base.payment_gateway_names || []).join(', '),
            order_status: base.db_order?.status_detail || base.status_detail || order.db_status,
            // A replacement reuses the original order's intake.
            intake: dbOrder.replacement_of ? null : intakeState(dbOrder),
            db_order: base.db_order || base,
          };
        });

        const supplementOrders = (supplementRes.status === 'fulfilled' ? supplementRes.value.data?.orders || [] : []).map(mapSupplementOrder);

        const merged = [...formattedOrders, ...supplementOrders].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setOrders(merged);
        return merged;
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load orders');
        return [];
      } finally {
        setLoading(false);
      }
    },
    [id]
  );

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // Reload and stay on the order being viewed, now carrying its replacement info.
  const refreshSelectedOrder = async () => {
    const viewedId = selectedOrder?.id;
    const fresh = await loadOrders({ fresh: true });
    setSelectedOrder(fresh.find((order) => order.id === viewedId) || null);
  };

  const orderNumberFor = (dbId) => orders.find((order) => order.dbId === dbId)?.order_number;
  const openOrderByDbId = (dbId) => {
    const target = orders.find((order) => order.dbId === dbId);
    if (target) setSelectedOrder(target);
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  const formatAddress = (address) => {
    if (!address) return 'N/A';
    const street = [address.address1, address.address2].filter(Boolean).join(', ');
    // Supplement addresses can be missing state/zip — drop empty parts instead of
    // printing stray commas.
    const cityLine = [address.city, [address.province_code, address.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    return [address.name, street, cityLine, address.country].filter(Boolean).join('\n') || 'N/A';
  };

  const tassoStatusFlow = [
    { code: 'accepted', label: 'Order Accepted' },
    { code: 'pendingfulfillment', label: 'At Tasso' },
    { code: 'inlogistics', label: 'In logistics' },
    { code: 'readytoship', label: 'Ready to Ship' },
    { code: 'atpatient', label: 'At Patient' },
    { code: 'intransittolab', label: 'In Transit To Lab' },
    { code: 'atlab', label: 'At Lab' },
    { code: 'resultsready', label: 'Results Ready' },
    { code: 'replaced', label: 'Replaced' },
  ];

  const getTassoStatusLabel = (tassoStatus) => {
    if (!tassoStatus) return null;

    const statusCode = typeof tassoStatus === 'string' ? tassoStatus.toLowerCase().trim() : tassoStatus.code?.toLowerCase().trim();

    const mappedStatus = tassoStatusFlow.find((status) => status.code.toLowerCase() === statusCode);

    return mappedStatus?.label || tassoStatus.label || statusCode;
  };

  // One-line intake summary for the Order Status column; the full picture is in
  // the detail timeline.
  const getIntakeLabel = (order) => {
    if (!order?.intake) return null;
    if (order.intake.submitted) return 'Intake: Submitted';
    if (order.intake.emailSent) return 'Intake: Email Sent';
    return 'Intake: Email Not Sent';
  };

  const getCurrentStatusLabel = (order) => {
    // Supplements aren't kit-tracked — their status is the OK Capsule fulfillment.
    if (order?.isSupplement) return SUPPLEMENT_STATUS_MAP[order.supplementStatus] || 'Processing';
    const mailmanStatus = order?.db_order?.mailman_status_detail;
    const tassoStatus = order?.db_order?.tasso_status_detail;
    const tassoLabel = getTassoStatusLabel(tassoStatus);
    const intakeLabel = getIntakeLabel(order);

    const kitStatus = (() => {
      if (mailmanStatus?.label && tassoLabel) {
        return (
          <>
            <div>Semen Analysis Kit: {mailmanStatus.label}</div>
            <div>Hormone Panel Kit: {tassoLabel}</div>
          </>
        );
      }
      if (mailmanStatus?.label) return mailmanStatus.label;
      if (tassoLabel) return tassoLabel;
      return 'Waiting for clinician to assign';
    })();

    if (!intakeLabel) return kitStatus;
    return (
      <>
        <div>{kitStatus}</div>
        <div className="intake-status-line">{intakeLabel}</div>
      </>
    );
  };

  const handleDownloadReport = (order) => {
    const doc = order?.db_order?.pdf_documents?.[0];
    const pdfUrl = doc?.download_url || doc?.view_url || doc?.presigned_url;

    if (!pdfUrl) {
      alert('No report available for this order.');
      return;
    }

    // Create a hidden link to trigger the download
    const link = document.createElement('a');
    link.href = pdfUrl;

    // Try to extract a filename from the URL or use a fallback
    const fileName = doc?.title || `order_${order.order_number}_report.pdf`;
    link.download = fileName;

    // For browsers that don't honor the download attribute (like Safari),
    // open in new tab instead
    link.target = '_blank';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleViewReport = (orderId, patientId) => {
    if (!orderId) {
      alert('Please select an order first.');
      return;
    }

    navigate('/report', {
      state: {
        orderId,
        userId: patientId, // 🧠 send the actual patient id
      },
    });
  };

  if (loading) return <div className="loader">Loading orders...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="order-container">
      {!selectedOrder ? (
        <>
          <div className="order-actions">
            <button className="btn back-button" onClick={() => navigate('/admin/patient-list')}>
              &larr; Back to Orders
            </button>
            {role == 'admin' && patient && (
              <h2 className="patient-name-heading">
                {patient.first_name} {patient.last_name}
              </h2>
            )}
          </div>
          {orders.length === 0 ? (
            <div className="no-orders">No orders found for this patient.</div>
          ) : (
            <table className="order-table">
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Order Date</th>
                  <th>Email</th>
                  <th>Total</th>
                  <th>Payment Status</th>
                  <th>Order Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id}>
                    <td>
                      {order.order_number}
                      {order.isSupplement && <span className="order-type-badge">Supplement</span>}
                      {order.isClinicOrder && <span className="order-type-badge">Clinic</span>}
                      {order.isReplacement && <span className="order-type-badge replacement-badge">Replacement</span>}
                    </td>
                    <td>{formatDate(order.created_at)}</td>
                    <td>{order.email}</td>
                    <td>
                      {order.currency === 'USD' ? '$' : order.currency} {order.total_price}
                    </td>
                    <td>
                      {order.b2bDetails || order.isReplacement
                        ? clinicPaymentPill(order).label
                        : order.financial_status?.charAt(0).toUpperCase() + order.financial_status?.slice(1).toLowerCase()}
                    </td>
                    <td>{getCurrentStatusLabel(order)}</td>
                    <td>
                      <button className="btn btn-primary view-details-btn" onClick={() => setSelectedOrder(order)}>
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      ) : (
        // ✅ Full Order Details
        <div key={selectedOrder.id} className="order-card full-view">
          {/* Action buttons for this specific order */}
          {role == 'admin' && patient && (
            <h2 className="patient-name-heading">
              {patient.first_name} {patient.last_name}
            </h2>
          )}
          <div className="order-actions">
            <button className="btn back-button" onClick={() => setSelectedOrder(null)}>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Orders
            </button>
            {/* Supplement orders have no lab report — nothing to view or download. */}
            {!selectedOrder.isSupplement && (
              <div className="report-actions">
                <button className="btn btn-primary" onClick={() => handleDownloadReport(selectedOrder)}>
                  Download Report
                </button>
                <button className="btn back-button" onClick={() => handleViewReport(selectedOrder.id, id)}>
                  View Report
                </button>
                {canReorderKit(selectedOrder) && (
                  <button className="btn back-button reorder-kit-btn" onClick={() => setShowReorderModal(true)}>
                    Re-order Kit
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Header */}
          <div className="order-header">
            <h2>
              Order #{selectedOrder.order_number}
              {selectedOrder.isSupplement && <span className="order-type-badge">Supplement</span>}
              {selectedOrder.isClinicOrder && <span className="order-type-badge">Clinic</span>}
              {selectedOrder.isReplacement && <span className="order-type-badge replacement-badge">Replacement</span>}
            </h2>
            <span className={`status ${clinicPaymentPill(selectedOrder).className}`}>{clinicPaymentPill(selectedOrder).label}</span>
          </div>
          {/* Kit/retest orders only — supplements aren't kit-tracked */}
          {!selectedOrder.isSupplement && (
            <StatusTimeline
              currentStatus={{
                mailman: selectedOrder?.db_order?.mailman_status_detail,
                tasso: selectedOrder?.db_order?.tasso_status_detail,
              }}
              intake={selectedOrder.intake}
              type={
                selectedOrder?.db_order?.tasso_status_detail && selectedOrder?.db_order?.mailman_status_detail
                  ? ['mailman', 'tasso']
                  : selectedOrder?.db_order?.type
              }
            />
          )}

          {/* Order Info */}
          <div className="order-info">
            <p>
              <strong>Order Date:</strong> {formatDate(selectedOrder.created_at)}
            </p>
            <p>
              <strong>Fulfillment:</strong> {selectedOrder.fulfillment_status}
            </p>
            <p>
              <strong>Email:</strong> <span className="black-text">{selectedOrder.email}</span>
            </p>
            {/* With b2b_details these move into the Clinic Order Details section below. */}
            {selectedOrder.isClinicOrder && !selectedOrder.b2bDetails && (
              <>
                {selectedOrder.clinicName && (
                  <p>
                    <strong>Clinic:</strong> {selectedOrder.clinicName}
                  </p>
                )}
                {selectedOrder.billingType && (
                  <p>
                    <strong>Billing:</strong> {BILLING_TYPE_LABELS[selectedOrder.billingType] || selectedOrder.billingType}
                  </p>
                )}
                {selectedOrder.paymentLink && (
                  <p>
                    <strong>Payment Link:</strong>{' '}
                    <a href={selectedOrder.paymentLink} target="_blank" rel="noreferrer" style={{ color: '#1E47E5', textDecoration: 'underline' }}>
                      Open Payment Link
                    </a>
                  </p>
                )}
              </>
            )}
            {selectedOrder.isSupplement && (
              <>
                {selectedOrder.planLabel && (
                  <p>
                    <strong>Plan:</strong> {selectedOrder.planLabel}
                  </p>
                )}
                {selectedOrder.subscriptionStatus && (
                  <p>
                    <strong>Subscription:</strong> <span className="capitalize-text">{selectedOrder.subscriptionStatus}</span>
                  </p>
                )}
                {selectedOrder.okcapsuleOrderName && (
                  <p>
                    <strong>OK Capsule Order:</strong> {selectedOrder.okcapsuleOrderName}
                  </p>
                )}
                {/* {selectedOrder.trackingNumber && (
                  <p>
                    <strong>Tracking:</strong>{' '}
                    {selectedOrder.trackingUrl ? (
                      <a href={selectedOrder.trackingUrl} target="_blank" rel="noreferrer">
                        {selectedOrder.trackingNumber}
                      </a>
                    ) : (
                      selectedOrder.trackingNumber
                    )}
                  </p>
                )} */}
              </>
            )}
          </div>

          {/* Supplement submission failure — admin-only detail */}
          {selectedOrder.isSupplement && selectedOrder.errorMessage && (
            <div className="supplement-error">
              <strong>Fulfillment error:</strong> {selectedOrder.errorMessage}
            </div>
          )}

          {/* Kit IDs / tracking numbers — kit and retest orders only */}
          {!selectedOrder.isSupplement && (
            <KitDetails dbOrder={selectedOrder.db_order} tassoLabel={getTassoStatusLabel(selectedOrder?.db_order?.tasso_status_detail)} />
          )}

          {!selectedOrder.isSupplement && (
            <ReplacementDetails
              dbOrder={selectedOrder.db_order}
              orderNumberFor={orderNumberFor}
              onOpenOrder={openOrderByDbId}
              onUpdated={refreshSelectedOrder}
            />
          )}

          {showReorderModal && (
            <ReorderKitModal
              order={selectedOrder}
              patient={patient}
              onClose={() => setShowReorderModal(false)}
              onIssued={() => {
                setShowReorderModal(false);
                refreshSelectedOrder();
              }}
            />
          )}

          {selectedOrder.isClinicOrder && (
            <ClinicOrderDetails
              details={selectedOrder.b2bDetails}
              paymentLink={selectedOrder.paymentLink}
              documents={selectedOrder.db_order?.pdf_documents || []}
            />
          )}

          {/* Addresses — supplements ship to the address on the patient record and
              have no separate billing snapshot */}
          {selectedOrder.isSupplement
            ? selectedOrder.shipping_address && (
                <div className="addresses">
                  <div>
                    <h4>Shipping Address</h4>
                    <pre>{formatAddress(selectedOrder.shipping_address)}</pre>
                  </div>
                </div>
              )
            : !selectedOrder.isRetest &&
              (selectedOrder.billing_address || selectedOrder.shipping_address) && (
                <div className="addresses">
                  {/* Clinic orders are billed off-storefront, so they carry no
                      billing snapshot — only render what the order has. */}
                  {selectedOrder.billing_address && (
                    <div>
                      <h4>Billing Address</h4>
                      <pre>{formatAddress(selectedOrder.billing_address)}</pre>
                    </div>
                  )}
                  {selectedOrder.shipping_address && (
                    <div>
                      <h4>Shipping Address</h4>
                      <pre>{formatAddress(selectedOrder.shipping_address)}</pre>
                    </div>
                  )}
                </div>
              )}

          {/* Products */}
          <div className="products">
            <h3>Products</h3>
            {selectedOrder.products.map((product) => (
              <div key={product.id} className="product-item">
                <div className="product-name">
                  {product.product_name}
                  {product.subtitle && <span className="product-subtitle">{product.subtitle}</span>}
                </div>
                <div className="product-qty">Qty: {product.quantity}</div>
                <div className="product-price">
                  {product.price !== '' && (
                    <>
                      {selectedOrder.currency === 'USD' ? '$' : selectedOrder.currency} {product.price}
                    </>
                  )}
                </div>
                <div className="product-discount">
                  Discount: {selectedOrder.currency === 'USD' ? '$' : selectedOrder.currency} {product.total_discount}
                </div>
              </div>
            ))}
          </div>

          {/* Shipments — a supplement billing cycle can ship as several OK Capsule
              orders (Starter tier caps each order at a 30-day pouch run) */}
          {selectedOrder.isSupplement && selectedOrder.shipments?.length > 0 && (
            <div className="shipments">
              <h3>Shipments</h3>
              <table className="details-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Supply</th>
                    <th>OK Capsule Order</th>
                    <th>Status</th>
                    {/* <th>Tracking</th> */}
                  </tr>
                </thead>
                <tbody>
                  {selectedOrder.shipments.map((shipment) => (
                    <tr key={shipment.sequence}>
                      <td>{shipment.sequence}</td>
                      <td>{shipment.supply_days ? `${shipment.supply_days} days` : '—'}</td>
                      <td>{shipment.okcapsule_order_name || '—'}</td>
                      <td>
                        {SUPPLEMENT_STATUS_MAP[shipment.fulfillment_status] || shipment.fulfillment_status}
                        {shipment.error_message && <div className="shipment-error">{shipment.error_message}</div>}
                      </td>
                      {/* <td>
                        {shipment.tracking_number ? (
                          shipment.tracking_url ? (
                            <a href={shipment.tracking_url} target="_blank" rel="noreferrer">
                              {shipment.tracking_number}
                            </a>
                          ) : (
                            shipment.tracking_number
                          )
                        ) : (
                          '—'
                        )}
                      </td> */}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Shipping — Shopify shipping lines; supplement and clinic orders have none */}
          {!selectedOrder.isSupplement && selectedOrder.shipping_lines?.length > 0 && (
            <div className="shipping">
              <h3>Shipping</h3>
              {selectedOrder.shipping_lines.map((line, i) => (
                <div key={i} className="shipping-item">
                  <div>{line.title}</div>
                  <div>
                    {selectedOrder.currency === 'USD' ? '$' : selectedOrder.currency} {line.price}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Totals */}
          <div className="totals">
            <h3>Payment & Totals</h3>
            <p>
              <strong>Payment:</strong> {selectedOrder.payment_gateway_names || 'N/A'}
            </p>
            {/* Supplements are billed by Stripe subscription — no Shopify tax/discount lines */}
            {!selectedOrder.isSupplement && (
              <>
                <p>
                  <strong>Total Discounts:</strong> {selectedOrder.currency === 'USD' ? '$' : selectedOrder.currency} {selectedOrder.total_discounts}
                </p>
                <p>
                  <strong>Total Tax:</strong> {selectedOrder.currency === 'USD' ? '$' : selectedOrder.currency} {selectedOrder.total_tax}
                </p>
              </>
            )}
            <div className="order-total">
              <strong>
                Total: {selectedOrder.currency === 'USD' ? '$' : selectedOrder.currency} {selectedOrder.total_price}
              </strong>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientOrders;
